import { useQuery, useMutation, useQueryClient } from 'react-query';
import ApiService from '@/services/api';
import {
  IProjectPhase,
  IProjectTask,
  IProjectCreateRequest,
  IProjectUpdateRequest,
  IPhaseCreateRequest,
  IPhaseUpdateRequest,
  ITaskCreateRequest,
  ITaskUpdateRequest,
  ITaskMoveRequest,
  IReorderRequest,
  IProjectListResponse,
  IProjectDetailResponse,
  IPhaseListResponse,
  ITaskListResponse,
  IDependencyCheckResponse,
  TaskStatus,
} from '@/types/project';
import {
  getProjectBOM,
  linkProjectBOM,
  ProjectBOMRequest,
  ProjectBOMResponse,
} from '@/services/bomService';

// ============================================================================
// Query Keys
// ============================================================================

export const PROJECT_QUERY_KEYS = {
  all: ['projects'] as const,
  list: () => [...PROJECT_QUERY_KEYS.all, 'list'] as const,
  detail: (id: string) => [...PROJECT_QUERY_KEYS.all, 'detail', id] as const,
  phases: (projectId: string) => [...PROJECT_QUERY_KEYS.all, 'phases', projectId] as const,
  tasks: (projectId: string) => [...PROJECT_QUERY_KEYS.all, 'tasks', projectId] as const,
  tasksByPhase: (projectId: string, phaseId: string) =>
    [...PROJECT_QUERY_KEYS.tasks(projectId), phaseId] as const,
  bom: (projectId: string) => [...PROJECT_QUERY_KEYS.all, 'bom', projectId] as const,
};

// ============================================================================
// Helper Functions
// ============================================================================

// Group tasks by status for Kanban view
export interface ITasksByStatus {
  plan: IProjectTask[];
  doing: IProjectTask[];
  review: IProjectTask[];
  done: IProjectTask[];
}

export const groupTasksByStatus = (tasks: IProjectTask[]): ITasksByStatus => {
  const grouped: ITasksByStatus = {
    plan: [],
    doing: [],
    review: [],
    done: [],
  };

  tasks.forEach((task) => {
    if (grouped[task.status]) {
      grouped[task.status].push(task);
    }
  });

  // Sort each group by sort_order
  Object.keys(grouped).forEach((status) => {
    grouped[status as TaskStatus].sort((a, b) => a.sort_order - b.sort_order);
  });

  return grouped;
};

// Group tasks by phase
export interface ITasksByPhase {
  [phaseId: string]: IProjectTask[];
}

export const groupTasksByPhase = (tasks: IProjectTask[]): ITasksByPhase => {
  const grouped: ITasksByPhase = {};

  tasks.forEach((task) => {
    if (!grouped[task.phase_id]) {
      grouped[task.phase_id] = [];
    }
    grouped[task.phase_id].push(task);
  });

  // Sort tasks within each phase by sort_order
  Object.keys(grouped).forEach((phaseId) => {
    grouped[phaseId].sort((a, b) => a.sort_order - b.sort_order);
  });

  return grouped;
};

// Calculate project progress based on phases
export const calculateProjectProgress = (phases: IProjectPhase[]): number => {
  if (phases.length === 0) return 0;
  const totalProgress = phases.reduce((sum, phase) => sum + phase.progress, 0);
  return Math.round(totalProgress / phases.length);
};

// Calculate phase progress based on tasks
export const calculatePhaseProgress = (tasks: IProjectTask[]): number => {
  if (tasks.length === 0) return 0;
  const completedTasks = tasks.filter((t) => t.status === 'done').length;
  return Math.round((completedTasks / tasks.length) * 100);
};

// ============================================================================
// Project Hooks
// ============================================================================

export const useProjects = () => {
  return useQuery<IProjectListResponse, Error>(
    PROJECT_QUERY_KEYS.list(),
    () => ApiService.getProjects(),
    {
      staleTime: 30000,
      cacheTime: 300000,
      refetchInterval: 60000, // Refresh every minute
    }
  );
};

export const useProject = (id: string) => {
  return useQuery<IProjectDetailResponse, Error>(
    PROJECT_QUERY_KEYS.detail(id),
    () => ApiService.getProject(id),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IProjectCreateRequest) => ApiService.createProject(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.list());
      },
    }
  );
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: IProjectUpdateRequest }) =>
      ApiService.updateProject(id, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.list());
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.detail(variables.id));
      },
    }
  );
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => ApiService.deleteProject(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.list());
      },
    }
  );
};

// ============================================================================
// Phase Hooks
// ============================================================================

export const usePhases = (projectId: string) => {
  return useQuery<IPhaseListResponse, Error>(
    PROJECT_QUERY_KEYS.phases(projectId),
    () => ApiService.getProjectPhases(projectId),
    {
      enabled: !!projectId,
      staleTime: 30000,
    }
  );
};

export const useCreatePhase = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ projectId, data }: { projectId: string; data: IPhaseCreateRequest }) =>
      ApiService.createProjectPhase(projectId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.phases(variables.projectId));
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.detail(variables.projectId));
      },
    }
  );
};

export const useUpdatePhase = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ projectId, phaseId, data }: { projectId: string; phaseId: string; data: IPhaseUpdateRequest }) =>
      ApiService.updateProjectPhase(projectId, phaseId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.phases(variables.projectId));
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.detail(variables.projectId));
      },
    }
  );
};

export const useDeletePhase = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ projectId, phaseId }: { projectId: string; phaseId: string }) =>
      ApiService.deleteProjectPhase(projectId, phaseId),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.phases(variables.projectId));
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.detail(variables.projectId));
      },
    }
  );
};

export const useReorderPhases = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ projectId, data }: { projectId: string; data: IReorderRequest }) =>
      ApiService.reorderProjectPhases(projectId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.phases(variables.projectId));
      },
    }
  );
};

// ============================================================================
// Task Hooks
// ============================================================================

export const useTasks = (projectId: string) => {
  return useQuery<ITaskListResponse, Error>(
    PROJECT_QUERY_KEYS.tasks(projectId),
    () => ApiService.getProjectTasks(projectId),
    {
      enabled: !!projectId,
      staleTime: 30000,
      refetchInterval: 60000, // Refresh every minute for team updates
    }
  );
};

export const useTasksByPhase = (projectId: string, phaseId: string) => {
  return useQuery<ITaskListResponse, Error>(
    PROJECT_QUERY_KEYS.tasksByPhase(projectId, phaseId),
    () => ApiService.getProjectTasksByPhase(projectId, phaseId),
    {
      enabled: !!projectId && !!phaseId,
      staleTime: 30000,
    }
  );
};

export const useCreateTask = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ projectId, data }: { projectId: string; data: ITaskCreateRequest }) =>
      ApiService.createProjectTask(projectId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.tasks(variables.projectId));
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.detail(variables.projectId));
      },
    }
  );
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ projectId, taskId, data }: { projectId: string; taskId: string; data: ITaskUpdateRequest }) =>
      ApiService.updateProjectTask(projectId, taskId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.tasks(variables.projectId));
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.detail(variables.projectId));
      },
    }
  );
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ projectId, taskId }: { projectId: string; taskId: string }) =>
      ApiService.deleteProjectTask(projectId, taskId),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.tasks(variables.projectId));
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.detail(variables.projectId));
      },
    }
  );
};

export const useMoveTask = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ projectId, taskId, data }: { projectId: string; taskId: string; data: ITaskMoveRequest }) =>
      ApiService.moveProjectTask(projectId, taskId, data),
    {
      onSuccess: (_response, variables) => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.tasks(variables.projectId));
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.detail(variables.projectId));
      },
    }
  );
};

export const useReorderTasks = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ projectId, phaseId, data }: { projectId: string; phaseId: string; data: IReorderRequest }) =>
      ApiService.reorderProjectTasks(projectId, phaseId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.tasks(variables.projectId));
      },
    }
  );
};

export const useCheckDependencies = (projectId: string, taskId: string) => {
  return useQuery<IDependencyCheckResponse, Error>(
    [...PROJECT_QUERY_KEYS.tasks(projectId), taskId, 'dependencies'] as const,
    () => ApiService.checkProjectTaskDependencies(projectId, taskId),
    {
      enabled: !!projectId && !!taskId,
      staleTime: 10000, // More frequent updates for dependency checks
    }
  );
};

// ============================================================================
// Project BOM Hooks
// ============================================================================

export const useProjectBOM = (projectId: string) => {
  return useQuery<ProjectBOMResponse, Error>(
    PROJECT_QUERY_KEYS.bom(projectId),
    () => getProjectBOM(projectId),
    {
      enabled: !!projectId,
      staleTime: 30000,
      retry: false, // Don't retry if no BOM is linked
    }
  );
};

export const useLinkProjectBOM = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ projectId, data }: { projectId: string; data: ProjectBOMRequest }) =>
      linkProjectBOM(projectId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.bom(variables.projectId));
        queryClient.invalidateQueries(PROJECT_QUERY_KEYS.detail(variables.projectId));
      },
    }
  );
};
